import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useApp } from "context/app";

import { ReactComponent as HelpIcon } from "assets/icons/menu/help.svg";
import { ReactComponent as TasksIcon } from "assets/icons/menu/tasks.svg";
import { ReactComponent as RocketIcon } from "assets/icons/menu/rocket.svg";
import { ReactComponent as WebsiteIcon } from "assets/icons/menu/website.svg";
import { ReactComponent as InvoicesIcon } from "assets/icons/menu/invoices.svg";
import { ReactComponent as SettingsIcon } from "assets/icons/menu/settings.svg";
import { ReactComponent as DashboardIcon } from "assets/icons/menu/dashboard.svg";
import { ReactComponent as QuestionIcon } from "assets/icons/menu/question.svg";
import { ReactComponent as ContactIcon } from "assets/icons/menu/contact.svg";
import { ReactComponent as NotificationIcon } from "assets/icons/menu/notification.svg";
import { ReactComponent as ReferralProgramIcon } from "assets/icons/menu/referral-program.svg";

const ClientMenu = (unreaded_count) => {
  const { user, websites, current_website, setCookie } = useApp();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const ref = useRef(null);
  const helpRef = useRef(null);

  const [website_extended, setWebsiteExtended] = useState(false);
  const [help_extended, setHelpExtended] = useState(false);

  let active = websites?.filter(
    (item) => item?.subscription?.status === "active",
  );
  let unactive = websites?.filter(
    (item) => item?.subscription?.status !== "active",
  );

  const formatted_website = [...active, ...unactive];

  const menu_items = [
    ...(!!user?.is_partner
      ? [
          {
            icon: <ReferralProgramIcon />,
            label: t("Referral program"),
            href: "/",
          },
        ]
      : []),
    {
      icon: <DashboardIcon />,
      label: t("Dashboard"),
      href: !!user?.is_partner ? "/dashboard" : "/",
    },
    {
      ref: ref,
      icon: <WebsiteIcon />,
      label: t("Websites"),
      extended: website_extended,
      onclick: () => setWebsiteExtended(true),
      onMouseEnter: () => {
        setHelpExtended(false);
        setWebsiteExtended(true);
      },
      href: websites?.length === 0 ? "/websites" : null,
      active: pathname.includes("websites"),
      extended_items: formatted_website?.map(
        ({ _id, website, status, subscription }) => ({
          label: website,
          onClick: () => {
            setCookie(`${user?._id}-website`, _id, { maxAge: 1707109200 });
            navigate(`/websites/${_id}`);
            setTimeout(() => {
              setWebsiteExtended(false);
            }, 0);
          },
          active: _id === current_website?._id,
          disabled: subscription?.status !== "active",
          status,
        }),
      ),
    },
    {
      icon: <TasksIcon />,
      label: t("Tasks"),
      href: "/tasks",
    },
    {
      icon: <InvoicesIcon />,
      label: t("Payments"),
      href: "/invoices",
    },
    {
      icon: <NotificationIcon />,
      label: t("Notifications"),
      href: "/notifications",
      count: unreaded_count,
    },
    ...(!!!user?.is_partner
      ? [
          {
            icon: <ReferralProgramIcon />,
            label: t("Referral program"),
            href: "/referral-program",
          },
        ]
      : []),
    {
      icon: <RocketIcon />,
      label: t("Need a new project?"),
      href: "/new-project",
    },
    {
      ref: helpRef,
      icon: <HelpIcon />,
      label: t("Help"),
      extended: help_extended,
      onclick: () => setHelpExtended(true),
      onMouseEnter: () => {
        setWebsiteExtended(false);
        setHelpExtended(true);
      },
      href: websites?.length === 0 ? "/contact" : null,
      active: pathname === "/faq" || pathname === "/contact",
      extended_items: [
        {
          icon: <QuestionIcon />,
          label: t("FAQ"),
          href: "/faq",
        },
        {
          icon: <ContactIcon />,
          label: t("Contact"),
          href: "/contact",
        },
      ].map((item) => ({
        ...item,
        onClick: () => {
          setTimeout(() => {
            setHelpExtended(false);
          }, 0);
        },
      })),
    },
    {
      icon: <SettingsIcon />,
      label: t("Settings"),
      href: "/settings",
    },
  ].map((item) => ({
    ...item,
    onMouseEnter:
      item?.onMouseEnter ||
      (() => {
        setWebsiteExtended(false);
        setHelpExtended(false);
      }),
  }));

  useEffect(() => {
    const sidebar = document.querySelector(".dashboard-layout-sidebar");
    const floatCards = document.querySelectorAll(".dashboard-menu__float-card");

    let cardFlag = false;

    const closeExtendedMenus = () => {
      setTimeout(() => {
        if (!cardFlag) {
          setWebsiteExtended(false);
          setHelpExtended(false);
        }
      }, 0);
    };

    const handleCardEnter = () => (cardFlag = true);

    const handleCardLeave = () => {
      cardFlag = false;

      closeExtendedMenus();
    };

    floatCards.forEach((card) => {
      card.addEventListener("mouseenter", handleCardEnter);
      card.addEventListener("mouseleave", handleCardLeave);
    });

    sidebar?.addEventListener("mouseleave", closeExtendedMenus);

    return () => {
      floatCards.forEach((card) => {
        card.removeEventListener("mouseenter", handleCardEnter);
        card.removeEventListener("mouseleave", handleCardLeave);
      });

      sidebar?.removeEventListener("mouseleave", closeExtendedMenus);
    };
  }, []);

  return menu_items;
};

export default ClientMenu;
